import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ganeshaShareImageRenderer from './utils/ganeshaShareImageRenderer';
import useLocale from '../../hooks/useLocale';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    padding: 0;
    align-items: stretch;
  }
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: ${p => p.theme.spacing[4]}px;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  text-align: center;
  width: 100%;

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }
`;

const Preview = styled.img`
  width: 100%;
  max-width: 360px;
  aspect-ratio: 1 / 1;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
`;

const Message = styled.p`
  color: ${p => p.theme.colors.text.secondary};
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const Btn = styled.button`
  padding: 10px 22px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  min-height: 44px;
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
  background: ${p => p.primary ? 'linear-gradient(135deg, #EF6C00, #D84315)' : '#fff'};
  color: ${p => p.primary ? '#fff' : '#8B3A00'};
  border: 1px solid #EF6C00;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const GaneshaShareImage = ({ open, onClose, cellIndex, reading, lang }) => {
  const t = useLocale(lang, 'ganeshaPrashnavali');
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [imageBlob, setImageBlob] = useState(null);
  const [rendering, setRendering] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setRendering(true);
      setError(null);
      try {
        const { dataUrl, blob } = await ganeshaShareImageRenderer(cellIndex, reading, lang);
        if (cancelled) return;
        setImageDataUrl(dataUrl);
        setImageBlob(blob);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to generate image');
      } finally {
        if (!cancelled) setRendering(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, cellIndex, reading, lang]);

  const handleShare = async () => {
    if (!imageBlob) return;
    const file = new File([imageBlob], 'ganesha-prashnavali.png', { type: 'image/png' });
    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: t.shareTitle || "Sri Ganesha's Guidance",
        });
      } catch {
        // user cancelled
      }
    } else {
      handleSave();
    }
  };

  const handleSave = () => {
    if (!imageDataUrl) return;
    const a = document.createElement('a');
    a.href = imageDataUrl;
    a.download = 'ganesha-prashnavali.png';
    a.click();
  };

  if (!open) return null;

  return (
    <Backdrop onClick={onClose} role="dialog" aria-modal="true">
      <Modal onClick={(e) => e.stopPropagation()}>
        {rendering && <Message>{t.rendering || 'Preparing your reading...'}</Message>}
        {error && <Message>{t.shareError || 'Unable to generate image'}: {error}</Message>}
        {imageDataUrl && <Preview src={imageDataUrl} alt={t.shareAlt || 'Share card preview'} />}

        <Actions>
          <Btn primary onClick={handleShare} disabled={!imageBlob}>
            {t.shareNative || 'Share'}
          </Btn>
          <Btn onClick={handleSave} disabled={!imageDataUrl}>
            {t.shareSave || 'Save to device'}
          </Btn>
          <Btn onClick={onClose}>
            {t.shareClose || 'Close'}
          </Btn>
        </Actions>
      </Modal>
    </Backdrop>
  );
};

export default GaneshaShareImage;
